/*
  Profile page for FullStack.cash - migrated from jwt-bch-frontend
*/

import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { Container, Row, Col, Card, Button, Alert, ListGroup } from 'react-bootstrap'
import { getUser, logout, getExpirationDate } from '../services/auth'
import { QRCode } from 'react-qr-code'
import ChangePassword from '../components/ChangePassword'
import NOTIFICATION from '../lib/notification'

const SERVER = process.env.REACT_APP_AUTHSERVER || process.env.AUTHSERVER
const Notification = new NOTIFICATION()

function Profile () {
  const navigate = useNavigate()
  const [userData, setUserData] = useState(null)
  const [credit, setCredit] = useState(0)
  const [apiToken, setApiToken] = useState('')
  const [bchAddr, setBchAddr] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const data = await getUser()

        if (!data || !data.userdata) {
          logout(() => navigate('/'))
          return
        }

        const hourDiff = await verifyTokenExpiration()
        if (hourDiff < 1) {
          logout(() => navigate('/'))
          return
        }

        setUserData(data)
        setApiToken(data.userdata?.user?.apiToken || '')
        setCredit(data.userdata?.user?.credit || 0)
        setBchAddr(data.userdata?.user?.bchAddr || '')
      } catch (err) {
        console.error('Error loading user data:', err)
        logout(() => navigate('/'))
      } finally {
        setLoading(false)
      }
    }

    loadUserData()
  }, [navigate])

  const verifyTokenExpiration = async () => {
    const result = await getExpirationDate()
    if (!result || !result.now || !result.exp) {
      return -1
    }
    const now = new Date(result.now)
    const exp = new Date(result.exp)
    const dateDiff = exp.getTime() - now.getTime()
    return Math.floor(dateDiff / 1000 / 60 / 60)
  }

  const handleGetJwt = async (e) => {
    e.preventDefault()
    try {
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userData.jwt}`
        }
      }
      const data = await fetch(`${SERVER}/apitoken/new`, options)

      if (data.status > 399) throw new Error('Could not get new JWT token.')

      const data2 = await data.json()
      setApiToken(data2.apiToken)
      Notification.notify('Success', 'New API token generated', 'success')
    } catch (err) {
      Notification.notify('Error', err.message, 'danger')
      setMessage(err.message)
    }
  }

  const handleUpdateCredit = async (e) => {
    e.preventDefault()
    try {
      const id = userData.userdata.user._id

      const options = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userData.jwt}`
        }
      }
      const data = await fetch(`${SERVER}/apitoken/update-credit/${id}`, options)
      const creditData = await data.json()
      setCredit(creditData)
      Notification.notify('Success', 'Credit updated', 'success')
    } catch (err) {
      Notification.notify('Error', err.message, 'danger')
      setMessage(err.message)
    }
  }

  if (loading) {
    return (
      <Container className='py-5'>
        <Row>
          <Col>
            <p>Loading...</p>
          </Col>
        </Row>
      </Container>
    )
  }

  if (!userData) {
    return null
  }

  return (
    <>
      <Helmet>
        <title>Profile - FullStack.cash</title>
      </Helmet>

      <Container className='py-5'>
        <Row>
          <Col md={8}>
            <Card>
              <Card.Body>
                <Card.Title><h2>Your Profile</h2></Card.Title>
                <ListGroup variant='flush'>
                  <ListGroup.Item>
                    <strong>Name:</strong> {userData.username || 'N/A'}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Email:</strong> {userData.email || 'N/A'}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>API JWT Token:</strong> {apiToken || 'N/A'}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Credit:</strong> ${credit}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>BCH Deposit Address:</strong> {bchAddr || 'N/A'}
                  </ListGroup.Item>
                </ListGroup>

                {bchAddr && (
                  <div className='text-center mt-3'>
                    <QRCode value={bchAddr} />
                  </div>
                )}

                <div className='mt-3 d-flex gap-2'>
                  <Button variant='primary' onClick={handleGetJwt}>
                    Get API Token
                  </Button>
                  <Button variant='secondary' onClick={handleUpdateCredit}>
                    Update Credit
                  </Button>
                </div>

                {message && (
                  <Alert variant='danger' className='mt-3'>{message}</Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <ChangePassword />
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default Profile
